/**
 * Top Projects Table Component
 *
 * Displays top projects by revenue with customer and salesperson info.
 */

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/frontend/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { Badge } from "@/frontend/components/ui/badge";
import type { ProjectInfo } from "@/shared/types/sales-analytics";
import { t } from "@/shared/lib/th-translations";

interface TopProjectsTableProps {
  projects: ProjectInfo[];
  title?: string;
  description?: string;
}

export function TopProjectsTable({ projects, title = t("topProjects.title"), description }: TopProjectsTableProps) {
  if (projects.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            {t("topProjects.noData")}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Rank</TableHead>
              <TableHead>Project ID</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Salesperson</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
              <TableHead>Category</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.map((project, index) => (
              <TableRow key={project.projectId}>
                <TableCell className="font-bold text-muted-foreground">
                  #{index + 1}
                </TableCell>
                <TableCell className="font-mono text-sm">
                  {project.projectId}
                </TableCell>
                <TableCell>{project.customerName}</TableCell>
                <TableCell>{project.salespersonName}</TableCell>
                <TableCell className="text-right font-semibold">
                  ฿{project.revenue.toLocaleString()}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{project.mainCategory}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
